import { useState } from "react";
import { Navigate, useNavigate } from "react-router";

import { ModelConfigColumn } from "@prequel/react";
import {
  Badge,
  BadgeColor,
  Button,
  ButtonStyle,
  ConfirmModal,
  DescriptionList,
  InlineText,
  Modal,
  PageHeading,
  Spinner,
  Table,
} from "@prequel/shared-components";

import { useDeleteModel, useModel, useValidateModel } from "@/api/queries/models";
import Breadcrumbs from "@/components/Breadcrumbs";
import DangerZone from "@/components/DangerZone";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import BufoPage from "@/pages/BufoPage";
import { isBufoUUID } from "@/pages/BufoPage/bufo";

const COLUMN_HEADERS = ["Name in Source", "Name in Destination", "Description", "Data Type"];

const ModelPage = () => {
  const modelID = useRequiredParam("modelID");
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: model, isLoading: isLoadingModel } = useModel(modelID);
  const validateModelMutation = useValidateModel();
  const deleteModelMutation = useDeleteModel();

  if (isLoadingModel) {
    return <Spinner />;
  }

  if (!model) {
    if (isBufoUUID(modelID)) {
      // Easter egg shows page with random bufo
      return <BufoPage />;
    }
    return <Navigate to="/models" replace />;
  }

  const rows = [...model.columns].map((column: ModelConfigColumn) => ({
    id: column.name_in_source,
    cells: [
      {
        value: (
          <>
            {column.name_in_source}
            {column.is_primary_key && <Badge text="Primary Key" color={BadgeColor.GREEN} className="ml-1" />}
            {column.is_last_modified && <Badge text="Last Modified" color={BadgeColor.GREEN} className="ml-1" />}
          </>
        ),
      },
      {
        value: column.name_in_destination,
      },
      {
        value: (
          <>
            {column.description !== "" ? (
              column.description
            ) : (
              <span className="grow text-gray-300 italic">undefined</span>
            )}
          </>
        ),
      },
      {
        value: <InlineText value={column.data_type} />,
      },
    ],
  }));

  const onConfirmDelete = () => {
    if (model) {
      deleteModelMutation.mutate({
        id: model.id,
        redirectPath: "/models",
      });
    }
    setShowDeleteModal(false);
  };

  const onValidate = () => {
    if (model) {
      validateModelMutation.mutate(model);
    }
  };

  const sourceQuery = (model.source_query || "").trim();

  const dangerZoneActions = [
    {
      label: "Delete Model",
      description: "Once you delete a model, the action cannot be undone.",
      buttonText: "Delete model",
      onClick: () => {
        setShowDeleteModal(true);
      },
    },
  ];

  return (
    <>
      <div className="max-w-4xl px-7 py-4">
        <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
          <ConfirmModal
            title="Delete model"
            message="Are you sure you want to delete this model? This action cannot be undone."
            onConfirm={onConfirmDelete}
            confirmButtonText="Delete"
            onCancel={() => setShowDeleteModal(false)}
          />
        </Modal>
        <PageHeading
          title={model?.model_name}
          onGoBack={() => navigate("/models")}
          breadcrumbs={<Breadcrumbs dynamicLabel={model?.model_name} />}
        >
          <Button
            text={
              validateModelMutation.isPending ? (
                <div className="flex">
                  <Spinner.Inline className="mr-3 text-emerald-600" /> Validating
                </div>
              ) : (
                "Validate Model"
              )
            }
            type={ButtonStyle.TERTIARY}
            onClick={onValidate}
          />
        </PageHeading>
        <div className="mt-10 divide-y divide-gray-200">
          <div className="space-y-1 pb-6">
            <h3 className="text-lg/6 font-medium text-gray-900">Details</h3>
          </div>
          <div>
            <DescriptionList>
              <DescriptionList.Item label="ID" value={model.id} />
              <DescriptionList.Item label="Description" value={model.description} />
              <DescriptionList.Item label="Source Table" value={model.source_table} />
              <DescriptionList.Item label="Source Schema" value={model.source_schema} />
              <DescriptionList.Item label="Organization Column" value={model.organization_column} />
              <DescriptionList.Item label="Source Name" value={model.source_name} />
              {sourceQuery ? <DescriptionList.CodeBlockItem label="Source Query" value={sourceQuery} /> : null}
            </DescriptionList>
          </div>
        </div>
        <div className="mt-10">
          <div className="space-y-1">
            <h3 className="text-lg/6 font-medium text-gray-900">Columns</h3>
          </div>
          <div className="mt-6">
            <Table columns={COLUMN_HEADERS} rows={rows} footer={`${rows.length} columns`} />
          </div>
        </div>
        <div className="mt-15">
          <DangerZone actions={dangerZoneActions} />
        </div>
      </div>
    </>
  );
};

export default ModelPage;
